import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import websocketService from '../services/websocketService';
import { 
  setBalance, updatePosition, removePosition, addHistoryOrder 
} from '../store/tradeSlice';

const useWebSocket = () => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastPrice, setLastPrice] = useState(null);
  const [lastTrade, setLastTrade] = useState(null);
  const [lastNotification, setLastNotification] = useState(null);
  const [error, setError] = useState(null);
  
  const priceListenersRef = useRef(new Map());
  const tradeListenersRef = useRef(new Set());
  const notificationListenersRef = useRef(new Set());

  // Initialize connection on mount
  useEffect(() => {
    websocketService.connect();
    
    // Status change listener
    const handleStatusChange = (status) => {
      setConnectionStatus(status);
      setIsConnected(status === 'connected');
    };
    
    websocketService.onStatusChange(handleStatusChange);
    
    // Price listener
    const handlePrice = (priceData) => {
      setLastPrice(priceData);
      if (priceListenersRef.current.has(priceData.symbol)) {
        priceListenersRef.current.get(priceData.symbol).forEach(cb => cb(priceData));
      }
    };
    
    // Trade listener
    const handleTrade = (tradeData) => {
      setLastTrade(tradeData);
      tradeListenersRef.current.forEach(cb => cb(tradeData));
    };
    
    // Notification listener
    const handleNotification = (notificationData) => {
      setLastNotification(notificationData);
      notificationListenersRef.current.forEach(cb => cb(notificationData));
    };

    // ─── Position closed (TP/SL hit or manual close) ───────────────
    const handlePositionClosed = (data) => {
      // Remove from portfolio
      const positionId = data.id || data.instrumentId;
      if (positionId) {
        dispatch(removePosition(positionId));
      }
      // Add to history
      dispatch(addHistoryOrder({
        id: data.id,
        instId: data.instrumentId,
        instrumentId: data.instrumentId,
        direction: data.direction,
        qty: data.quantity || data.qty,
        entryPrice: data.entryPrice,
        exitPrice: data.exitPrice || data.executionPrice,
        pnl: data.pnl,
        swap: data.swap || 0,
        commission: data.commission || 0,
        sl: data.sl || 0,
        tp: data.tp || 0,
        closeReason: data.closeReason || 'WebTrader',
        openTime: data.openTime,
        closeTime: data.closeTime || new Date().toISOString(),
        cat: data.category,
        category: data.category,
      }));
    };

    // ─── Balance update ────────────────────────────────────────────
    const handleBalanceUpdate = (data) => {
      const balance = typeof data === 'number' ? data : data.balance;
      if (balance !== undefined) {
        dispatch(setBalance(balance));
      }
    };

    // ─── Position update (real-time P&L changes) ───────────────────
    const handlePositionUpdate = (data) => {
      if (data && data.instrumentId) {
        dispatch(updatePosition({
          instrumentId: data.instrumentId,
          position: data
        }));
      }
    };

    // ─── Trade notification (from trade_executed events) ───────────
    const handleTradeNotification = (data) => {
      if (data.type === 'close_position') {
        // Already handled by position_closed, but ensure consistency
      } else {
        // New trade opened
        tradeListenersRef.current.forEach(cb => cb(data));
      }
    };
    
    websocketService.on('price', handlePrice);
    websocketService.on('trade', handleTrade);
    websocketService.on('notification', handleNotification);
    websocketService.on('position_closed', handlePositionClosed);
    websocketService.on('balance_update', handleBalanceUpdate);
    websocketService.on('position_update', handlePositionUpdate);
    websocketService.on('trade_notification', handleTradeNotification);
    
    return () => {
      websocketService.off('price', handlePrice);
      websocketService.off('trade', handleTrade);
      websocketService.off('notification', handleNotification);
      websocketService.off('position_closed', handlePositionClosed);
      websocketService.off('balance_update', handleBalanceUpdate);
      websocketService.off('position_update', handlePositionUpdate);
      websocketService.off('trade_notification', handleTradeNotification);
    };
  }, [dispatch]);

  // Subscribe to instrument prices
  const subscribeToInstrument = useCallback((symbol) => {
    if (!symbol) return;
    websocketService.subscribe(symbol);
  }, []);

  // Subscribe to multiple instruments
  const subscribeToInstruments = useCallback((symbols) => {
    if (!symbols || symbols.length === 0) return;
    websocketService.subscribe(symbols);
  }, []);

  // Unsubscribe from instrument
  const unsubscribeFromInstrument = useCallback((symbol) => {
    if (!symbol) return;
    websocketService.unsubscribe(symbol);
  }, []);

  // Unsubscribe from multiple instruments
  const unsubscribeFromInstruments = useCallback((symbols) => {
    if (!symbols || symbols.length === 0) return;
    websocketService.unsubscribe(symbols);
  }, []);

  // Add price listener for specific instrument
  const onPriceUpdate = useCallback((symbol, callback) => {
    if (!priceListenersRef.current.has(symbol)) {
      priceListenersRef.current.set(symbol, new Set());
    }
    priceListenersRef.current.get(symbol).add(callback);
    
    return () => {
      if (priceListenersRef.current.has(symbol)) {
        priceListenersRef.current.get(symbol).delete(callback);
        if (priceListenersRef.current.get(symbol).size === 0) {
          priceListenersRef.current.delete(symbol);
        }
      }
    };
  }, []);

  // Add trade listener
  const onTradeUpdate = useCallback((callback) => {
    tradeListenersRef.current.add(callback);
    return () => tradeListenersRef.current.delete(callback);
  }, []);

  // Add notification listener
  const onNotification = useCallback((callback) => {
    notificationListenersRef.current.add(callback);
    return () => notificationListenersRef.current.delete(callback);
  }, []);

  // Subscribe to user updates
  const subscribeToUser = useCallback((userId) => {
    if (!userId) return;
    websocketService.subscribeUser(userId);
  }, []);

  // Unsubscribe from user updates
  const unsubscribeFromUser = useCallback((userId) => {
    if (!userId) return;
    websocketService.unsubscribeUser(userId);
  }, []);

  // Reconnect manually
  const reconnect = useCallback(() => {
    websocketService.reconnect();
  }, []);

  return {
    isConnected,
    connectionStatus,
    lastPrice,
    lastTrade,
    lastNotification,
    error,
    subscribeToInstrument,
    subscribeToInstruments,
    unsubscribeFromInstrument,
    unsubscribeFromInstruments,
    onPriceUpdate,
    onTradeUpdate,
    onNotification,
    subscribeToUser,
    unsubscribeFromUser,
    reconnect,
  };
};

export default useWebSocket;